/**
 * スタッフ個人設定を取得・適用するカスタムフック
 */

import { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';

interface StaffPreferences {
  staff_id: number;
  pos_exact_button_display: 'text' | 'amount';
  pos_calculator_layout: string;
  pos_sound_enabled: boolean;
  pos_confirmation_dialogs: boolean;
  pos_auto_print_receipt: boolean;
  ui_theme: 'light' | 'dark' | 'auto';
  ui_font_size: 'small' | 'medium' | 'large' | 'extra-large';
  ui_button_size: 'small' | 'medium' | 'large';
  ui_show_shortcuts: boolean;
  ui_animation_enabled: boolean;
  display_show_customer_info: boolean;
  display_show_stock_alerts: boolean;
  notification_bento_orders: boolean;
  notification_sound_type: string;
  notification_volume: number;
}

const defaultPreferences: StaffPreferences = {
  staff_id: 0,
  pos_exact_button_display: 'text',
  pos_calculator_layout: 'default',
  pos_sound_enabled: true,
  pos_confirmation_dialogs: true,
  pos_auto_print_receipt: true,
  ui_theme: 'light',
  ui_font_size: 'medium',
  ui_button_size: 'medium',
  ui_show_shortcuts: true,
  ui_animation_enabled: true,
  display_show_customer_info: true,
  display_show_stock_alerts: true,
  notification_bento_orders: true,
  notification_sound_type: 'default',
  notification_volume: 70,
};

export const useStaffPreferences = (staffId?: number | null) => {
  const [preferences, setPreferences] = useState<StaffPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (staffId) {
      fetchPreferences(staffId);
    } else {
      // ログインユーザーの設定を取得
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user.id) {
            fetchPreferences(user.id);
          }
        } catch (e) {
          console.error('ユーザー情報の解析エラー:', e);
        }
      }
    }
  }, [staffId]);

  const fetchPreferences = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/staff-preferences/${id}`);
      setPreferences(response.data);

      // UI設定を適用
      applyUISettings(response.data);

    } catch (err) {
      console.error('設定取得エラー:', err);
      setError('設定の取得に失敗しました');
      setPreferences(defaultPreferences);
    } finally {
      setLoading(false);
    }
  };

  // UI設定を実際に適用
  const applyUISettings = (prefs: StaffPreferences) => {
    // フォントサイズの適用
    const fontSizeMap = {
      'small': '14px',
      'medium': '16px',
      'large': '18px',
      'extra-large': '20px'
    };

    if (prefs.ui_font_size) {
      document.documentElement.style.setProperty('--base-font-size', fontSizeMap[prefs.ui_font_size]);
    }

    // テーマの適用
    if (prefs.ui_theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (prefs.ui_theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (prefs.ui_theme === 'auto') {
      // システムの設定に従う
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // ボタンサイズの適用
    const buttonSizeMap = {
      'small': 'scale-90',
      'medium': 'scale-100',
      'large': 'scale-110'
    };

    if (prefs.ui_button_size) {
      document.documentElement.setAttribute('data-button-size', buttonSizeMap[prefs.ui_button_size]);
    }

    // アニメーションの有効/無効
    if (!prefs.ui_animation_enabled) {
      document.documentElement.classList.add('no-animation');
    } else {
      document.documentElement.classList.remove('no-animation');
    }
  };

  // 設定の更新
  const updatePreference = async (key: keyof StaffPreferences, value: any) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);

    // UIに関する設定は即座に適用
    applyUISettings(newPrefs);

    // サーバーに保存（非同期）
    if (preferences.staff_id) {
      try {
        await apiClient.patch(`/staff-preferences/${preferences.staff_id}/${key}`, { value });
      } catch (err) {
        console.error('設定更新エラー:', err);
      }
    }
  };

  // 「ちょうど」ボタンの表示テキストを取得
  const getExactButtonText = (amount: number): string => {
    if (preferences.pos_exact_button_display === 'amount') {
      return `¥${amount.toLocaleString()}`;
    }
    return 'ちょうど';
  };

  // 操作音を再生
  const playSound = (soundType: 'click' | 'success' | 'error' | 'notification') => {
    if (!preferences.pos_sound_enabled) return;

    // 音声ファイルのパスを定義（実際の音声ファイルが必要）
    const soundMap = {
      'click': '/sounds/click.mp3',
      'success': '/sounds/success.mp3',
      'error': '/sounds/error.mp3',
      'notification': '/sounds/notification.mp3'
    };

    try {
      const audio = new Audio(soundMap[soundType]);
      audio.volume = preferences.notification_volume / 100;
      audio.play().catch(e => console.error('音声再生エラー:', e));
    } catch (e) {
      console.error('音声ファイルエラー:', e);
    }
  };

  return {
    preferences,
    loading,
    error,
    updatePreference,
    getExactButtonText,
    playSound,
    applyUISettings
  };
};

export default useStaffPreferences;